import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { Button, ValidatedInput } from '../../components'
import CountryPickerModal from '../CountryPickerModal'
import countries from '../../countries.json'
import { isEmailValid, isPasswordValid, isTelephoneValid } from '../../helpers'
import { appsUsernameSelected, defaultPhoneCountryCode } from '../../helpers/app'
import { ICountry } from '../../models/country'
import { LiteralsService } from '../../services'
import {
  setSignUpCallingCodeAction,
  setSignUpCountryAction,
  setSignUpEmailAction,
  setSignUpPasswordAction,
  setSignUpPhoneAction,
  setSignUpRepeatEmailAction,
  setSignUpRepeatPasswordAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'

// Brands that register by phone ask for it instead of the email, as the app does.
const isPhoneSignUp = appsUsernameSelected?.username === 'phone'

interface IOwnProps {
  // The host decides where "continue" leads: the next page, or the next step inside a modal.
  onContinue: () => void
  onSignIn: () => void
}

interface IProps extends IOwnProps, DispatchProp<AnyAction> {
  language: string
  email: string
  password: string
  repeatEmail: string
  repeatPassword: string
  phone: string
  country?: ICountry
}

/**
 * The first step of the sign up — credentials — as a form on its own, so the sign up page and
 * the prompt shown to guests render the very same fields and rules. Only the fields and the
 * actions: the host puts the chrome (card, title, back) around them.
 */
class SignUpStep1Form extends PureComponent<IProps> {
  private get buttonIsDisabled(): boolean {
    const { email, password, phone, repeatEmail, repeatPassword } = this.props
    // No repeat field for the phone, matching the app.
    const isUsernameInvalid = isPhoneSignUp ? !isTelephoneValid(phone) : !isEmailValid(email) || email !== repeatEmail

    return (
      isUsernameInvalid ||
      !isPasswordValid(password) ||
      !isPasswordValid(repeatPassword) ||
      password !== repeatPassword
    )
  }

  private get passwordAlert(): ReactNode {
    const { password } = this.props

    return (
      password !== '' &&
      !isPasswordValid(password) && (
        <div className="error-container">
          <span className="error">
            { LiteralsService.get('validPassword', true) }
          </span>
        </div>
      )
    )
  }

  constructor(props: IProps) {
    super(props)

    if (isPhoneSignUp && !props.country) {
      const defaultCountry = countries.find(country => country.code === defaultPhoneCountryCode)
      if (defaultCountry) {
        props.dispatch(setSignUpCountryAction(defaultCountry))
        props.dispatch(setSignUpCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get usernameFields(): ReactNode {
    const { email, repeatEmail, phone, country } = this.props

    if (isPhoneSignUp) {
      return (
        <ValidatedInput
          name="phone"
          phone
          label={ LiteralsService.get('phone', true) }
          value={ phone }
          country={ country }
          onChange={ this.onPhoneChange }
          error={ !isTelephoneValid(phone) }
        />
      )
    }

    return (
      <>
        <ValidatedInput
          name="email"
          label={ LiteralsService.get('email', true) }
          value={ email }
          onChange={ this.onEmailChange }
          error={ !isEmailValid(email) }
        />
        <ValidatedInput
          name="repeat-email"
          label={ LiteralsService.get('repeatEmail', true) }
          value={ repeatEmail }
          onChange={ this.onRepeatEmailChange }
          error={ !isEmailValid(repeatEmail) || email !== repeatEmail }
        />
      </>
    )
  }

  public render(): ReactNode {
    const { password, repeatPassword } = this.props

    return (
      <>
        <section>
          { this.usernameFields }
          <ValidatedInput
            name="password"
            label={ LiteralsService.get('password', true) }
            value={ password }
            onChange={ this.onPasswordChange }
            error={ !isPasswordValid(password) }
            isPassword
          />
          { this.passwordAlert }
          <ValidatedInput
            name="repeat-password"
            label={ LiteralsService.get('repeatPassword', true) }
            value={ repeatPassword }
            onChange={ this.onRepeatPasswordChange }
            error={ !isPasswordValid(repeatPassword) || password !== repeatPassword }
            isPassword
          />
        </section>
        <section className="actions">
          <Button
            title={ LiteralsService.get('continue') }
            onPress={ this.props.onContinue }
            filled
            upper
            isDisabled={ this.buttonIsDisabled }
          />
          <div className="link">
            <span onClick={ this.props.onSignIn }>{ this.alreadyHaveAccount }</span>
          </div>
        </section>
        { isPhoneSignUp && <CountryPickerModal selectCountry={ this.onCountryChange } selectedCountry={ this.props.country } /> }
      </>
    )
  }

  // "Already a member? Sign in.": the call to action after the question stands out in bold.
  // Some languages only ask the question; Greek closes it with ";".
  private get alreadyHaveAccount(): ReactNode {
    const sentence = LiteralsService.get('alreadyHaveAccount', true)
    const match = /^(.*?[?;])\s+(\S.*)$/.exec(sentence)
    if (!match) return sentence
    return <>{ match[1] } <strong>{ match[2] }</strong></>
  }

  private onEmailChange = (email: string) => {
    this.props.dispatch(setSignUpEmailAction(email))
  }

  private onPhoneChange = (phone: string) => {
    this.props.dispatch(setSignUpPhoneAction(phone))
  }

  private onCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setSignUpCountryAction(country))
    dispatch(setSignUpCallingCodeAction(country.dial_code))
  }

  private onPasswordChange = (password: string) => {
    this.props.dispatch(setSignUpPasswordAction(password))
  }

  private onRepeatEmailChange = (repeatEmail: string) => {
    this.props.dispatch(setSignUpRepeatEmailAction(repeatEmail))
  }

  private onRepeatPasswordChange = (repeatPassword: string) => {
    this.props.dispatch(setSignUpRepeatPasswordAction(repeatPassword))
  }
}

const mapStateToProps = ({ signUp, configuration }: IStoreState) => ({
  language: configuration.language,
  email: signUp.email,
  password: signUp.password,
  repeatEmail: signUp.repeatEmail,
  repeatPassword: signUp.repeatPassword,
  phone: signUp.phone,
  country: signUp.country
})

export default connectRedux(mapStateToProps, SignUpStep1Form) as React.ComponentType<IOwnProps>
